'use client';

import { useEffect, useState } from 'react';
import { X, Download, ExternalLink, Eye, FileText, Maximize2, Minimize2 } from 'lucide-react';

interface CVModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
}

export default function CVModal({ isOpen, onClose, onDownload }: CVModalProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const cvPath = '/assets/documents/Marwan-Hussein-CV.pdf';

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleOpenNewTab = () => {
    window.open(cvPath, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-background/80 backdrop-blur-md animate-fade-in">
      {/* Backdrop click listener */}
      <div
        className="absolute inset-0 z-0"
        onClick={onClose}
        aria-label="Close modal background"
      />

      {/* Modal Content */}
      <div
        className={`relative z-10 w-full transition-all duration-300 bg-surface/95 border border-sand/25 rounded-2xl shadow-warm-lg flex flex-col overflow-hidden ${
          isFullscreen ? 'h-full max-w-full rounded-none' : 'max-w-5xl h-[88vh] sm:h-[90vh]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-parchment/10 bg-surface-2/80 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sand/10 border border-sand/25 flex items-center justify-center text-sand">
              <FileText size={18} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display font-600 text-parchment text-sm sm:text-base tracking-wide">
                  Marwan-Hussein-CV
                </h3>
                <span className="hidden sm:inline-block px-2 py-0.5 rounded-full text-[10px] font-mono bg-sand/10 border border-sand/20 text-sand">
                  PDF
                </span>
              </div>
              <p className="text-parchment-muted text-xs font-mono">Marwan-Hussein-CV.pdf</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Download Button */}
            <button
              onClick={onDownload}
              className="btn-primary py-2 px-4 text-xs sm:text-sm"
              title="Download CV"
            >
              <Download size={14} />
              <span>Download CV</span>
            </button>

            {/* Open in new tab */}
            <button
              onClick={handleOpenNewTab}
              className="btn-ghost py-2 px-3 text-xs sm:text-sm"
              title="Open in new tab"
            >
              <ExternalLink size={14} />
              <span className="hidden sm:inline">Open in Tab</span>
            </button>

            {/* Toggle Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="hidden md:flex items-center justify-center p-2 rounded-lg bg-parchment/5 border border-parchment/10 text-parchment-muted hover:text-parchment transition-all duration-200"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-parchment/5 border border-parchment/10 text-parchment-muted hover:bg-rose/10 hover:border-rose/30 hover:text-rose transition-all duration-200"
              aria-label="Close CV Modal"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Embedded Document Viewer */}
        <div className="relative flex-1 w-full bg-slate-950/80 overflow-hidden">
          <iframe
            src={`${cvPath}#toolbar=1&view=FitH`}
            className="w-full h-full border-0 bg-surface-2"
            title="Marwan Hussein CV"
          />

          {/* Bottom notice bar */}
          <div className="absolute bottom-0 inset-x-0 bg-surface/90 backdrop-blur-md border-t border-parchment/10 px-4 py-2 flex items-center justify-between text-xs text-parchment-muted">
            <span className="flex items-center gap-1.5 font-mono">
              <Eye size={13} className="text-sand" />
              PDF viewer
            </span>
            <button
              onClick={onDownload}
              className="text-sand hover:underline font-mono text-[11px] flex items-center gap-1"
            >
              Need offline copy? Click to Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
